import { CollectorEvent } from './types';
import { clickOffset, describe, keyClass, now } from './util';

type Emit = (e: CollectorEvent) => void;

const MOVE_MIN_GAP_MS = 16;
// Fields whose input lengths are never sent in full: passwords, card details, one-time codes
const SENSITIVE_AUTOCOMPLETE = /^(cc-|one-time-code|current-password|new-password)/;

const isSensitive = (target: EventTarget | null): boolean => {
  const el = target as HTMLInputElement | null;
  if (!el || typeof el.getAttribute !== 'function') return false;
  if (el.type === 'password') return true;
  return (el.getAttribute('autocomplete') ?? '').trim().split(/\s+/).some((t) => SENSITIVE_AUTOCOMPLETE.test(t));
};
const SCROLL_MIN_GAP_MS = 50;

/**
 * Attaches capture-phase listeners for input, focus, scroll and page-lifecycle
 * events. Records types, positions and timing; never values or text.
 * Returns a function that removes them.
 */
export const attachListeners = (emit: Emit): (() => void) => {
  const removers: Array<() => void> = [];
  const on = <K extends string>(
    target: Window | Document,
    type: K,
    fn: (e: any) => void,
  ) => {
    target.addEventListener(type, fn, { capture: true, passive: true });
    removers.push(() => target.removeEventListener(type, fn, { capture: true }));
  };

  let lastMove = 0;
  on(window, 'pointermove', (e: PointerEvent) => {
    const t = now();
    if (t - lastMove < MOVE_MIN_GAP_MS) return;
    lastMove = t;
    emit({
      type: 'pm', t, x: e.clientX, y: e.clientY, sx: e.screenX, sy: e.screenY,
      mx: e.movementX, my: e.movementY, pt: e.pointerType, tr: e.isTrusted,
    });
  });

  for (const type of ['pointerdown', 'pointerup'] as const) {
    on(window, type, (e: PointerEvent) => {
      emit({
        type: type === 'pointerdown' ? 'pd' : 'pu', t: now(), x: e.clientX, y: e.clientY,
        sx: e.screenX, sy: e.screenY, b: e.button, pt: e.pointerType,
        pr: e.pressure, w: e.width, h: e.height, tr: e.isTrusted, el: describe(e.target),
      });
    });
  }

  on(window, 'click', (e: MouseEvent) => {
    emit({
      type: 'click', t: now(), x: e.clientX, y: e.clientY, sx: e.screenX, sy: e.screenY,
      d: e.detail, tr: e.isTrusted, el: describe(e.target),
      off: clickOffset(e.target, e.clientX, e.clientY),
    });
  });

  for (const type of ['keydown', 'keyup'] as const) {
    on(window, type, (e: KeyboardEvent) => {
      emit({
        type: type === 'keydown' ? 'kd' : 'ku', t: now(), k: keyClass(e.key),
        r: e.repeat, tr: e.isTrusted, el: describe(e.target),
      });
    });
  }

  on(window, 'beforeinput', (e: InputEvent) => {
    emit({ type: 'bi', t: now(), it: e.inputType, tr: e.isTrusted, el: describe(e.target) });
  });

  // No field lengths: only how much one input event inserted, and on sensitive fields
  // only whether that was one character or more (all the judge needs)
  on(window, 'input', (e: InputEvent) => {
    const dl = e.data?.length ?? null;
    emit({
      type: 'in', t: now(), it: e.inputType, dl: dl !== null && isSensitive(e.target) ? Math.min(dl, 2) : dl,
      tr: e.isTrusted, el: describe(e.target),
    });
  });

  on(window, 'paste', (e: ClipboardEvent) => {
    emit({ type: 'paste', t: now(), tr: e.isTrusted, el: describe(e.target) });
  });

  on(window, 'change', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    emit({
      type: 'change', t: now(), tr: e.isTrusted, el: describe(e.target),
      files: target?.files ? target.files.length : undefined,
    });
  });

  on(window, 'focusin', (e: FocusEvent) => {
    emit({ type: 'fi', t: now(), tr: e.isTrusted, el: describe(e.target) });
  });
  on(window, 'focusout', (e: FocusEvent) => {
    emit({ type: 'fo', t: now(), tr: e.isTrusted, el: describe(e.target) });
  });

  on(window, 'submit', (e: SubmitEvent) => {
    emit({ type: 'submit', t: now(), tr: e.isTrusted, el: describe(e.target) });
  });

  let lastScroll = 0;
  on(window, 'scroll', () => {
    const t = now();
    if (t - lastScroll < SCROLL_MIN_GAP_MS) return;
    lastScroll = t;
    emit({ type: 'scroll', t, x: Math.round(window.scrollX), y: Math.round(window.scrollY) });
  });

  on(window, 'wheel', (e: WheelEvent) => {
    emit({ type: 'wheel', t: now(), dx: e.deltaX, dy: e.deltaY, m: e.deltaMode, tr: e.isTrusted });
  });

  on(document, 'visibilitychange', () => {
    emit({ type: 'vis', t: now(), s: document.visibilityState });
  });
  on(window, 'focus', () => emit({ type: 'wfocus', t: now() }));
  on(window, 'blur', () => emit({ type: 'wblur', t: now() }));
  on(window, 'resize', () => {
    emit({ type: 'resize', t: now(), iw: window.innerWidth, ih: window.innerHeight });
  });

  return () => removers.forEach((r) => r());
};
